import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

const DRAG_HANDLE_KEY = new PluginKey('dragHandle');

/** Creates the drag handle DOM element with ⠿ grip icon. */
function createHandleEl(): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('data-drag-handle', '');
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('draggable', 'true');
  el.title = 'Drag to reorder';
  el.style.cssText = [
    'position:absolute',
    'left:-20px',
    'top:0',
    'width:16px',
    'height:16px',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'cursor:grab',
    'opacity:0',
    'transition:opacity 150ms',
    'color:var(--muted-foreground,#94a3b8)',
    'font-size:14px',
    'user-select:none',
    'pointer-events:all',
  ].join(';');
  el.textContent = '⠿';
  return el;
}

/**
 * Drag handle extension using @atlaskit/pragmatic-drag-and-drop.
 * Shows a ⠿ handle on block hover; full block reordering is deferred (see tasks/current-story.md).
 */
export function buildDragHandleExtension() {
  return Extension.create({
    name: 'dragHandle',

    addProseMirrorPlugins() {
      let handleEl: HTMLElement | null = null;
      let cleanup: (() => void) | null = null;
      let currentTarget: Element | null = null;

      const showHandle = (target: Element, editorView: { dom: HTMLElement }) => {
        if (!handleEl) {
          handleEl = createHandleEl();
          editorView.dom.style.position = 'relative';
          editorView.dom.parentElement?.appendChild(handleEl);

          cleanup = draggable({
            element: handleEl,
            getInitialData: () => ({ type: 'block-drag-handle' }),
            onGenerateDragPreview: ({ nativeSetDragImage }) => {
              nativeSetDragImage?.(target as HTMLElement, 0, 0);
            },
          });
        }

        const rect = target.getBoundingClientRect();
        const editorRect = editorView.dom.getBoundingClientRect();
        handleEl.style.top = `${rect.top - editorRect.top + editorView.dom.scrollTop}px`;
        handleEl.style.opacity = '1';
        currentTarget = target;
      };

      const hideHandle = () => {
        if (handleEl) handleEl.style.opacity = '0';
        currentTarget = null;
      };

      return [
        new Plugin({
          key: DRAG_HANDLE_KEY,

          props: {
            handleDOMEvents: {
              mouseover(_view, event) {
                const target = (event.target as Element)?.closest(
                  'p, h1, h2, h3, h4, h5, h6, ul, ol, li, blockquote, pre, table, [data-callout], [data-expandable], hr',
                );
                if (!target || target === currentTarget) return false;
                showHandle(target, _view);
                return false;
              },

              mouseleave(_view, event) {
                const related = event.relatedTarget as Element | null;
                if (related?.closest('[data-drag-handle]')) return false;
                hideHandle();
                return false;
              },
            },
          },

          destroy() {
            cleanup?.();
            handleEl?.remove();
            handleEl = null;
            cleanup = null;
            currentTarget = null;
          },
        }),
      ];
    },
  });
}
